<template>
    <div class="page-container">
        <div class="fixed-nav-container">
            <van-nav-bar left-text="返回" left-arrow @click-left="backToPreviousPage" title="我的比赛"
                class="custom-nav-bar" />
        </div>
        <div class="match-list">
            <van-pull-refresh v-model="isRefreshing" @refresh="onRefresh" :head-height="80" success-text="刷新成功">
                <van-list v-model:loading="loading" :finished="finished" @load="onLoad" finished-text="没有更多了"
                    :error="error" error-text="请求失败，点击重试" @error="onLoad">
                    <!-- 空状态显示 -->
                    <van-empty v-if="matches.length === 0 && !loading" description="暂无比赛记录">
                        <template #image>
                            <van-icon name="medal-o" size="64" color="#969799" />
                        </template>
                    </van-empty>
                    <van-card v-for="match in matches" :key="match.id" class="match-card"
                        @click="navigateToScore(match.id)">
                        <template #title>
                            <div class="match-title">
                                <span class="match-name">{{ match.name }}</span>
                                <van-tag :type="getStatusType(match.matchSubjectState)" class="status-tag">
                                    {{ match.matchSubjectStateStr }}
                                </van-tag>
                            </div>
                        </template>
                        <template #desc>
                            <div class="match-info">
                                <div class="info-row">
                                    <van-icon name="shop-o" class="info-icon" color="#666666" />
                                    <span>{{ match.merchantName }}</span>
                                </div>
                                <div class="info-row">
                                    <van-icon name="clock-o" class="info-icon" color="#666666" />
                                    <span>{{ formatDateTime(match.startDateTime) }}</span>
                                </div>
                                <div class="info-row">
                                    <van-icon name="label-o" class="info-icon" color="#666666" />
                                    <span>{{ match.matchItemTypeStr }}</span>
                                </div>
                            </div>
                        </template>
                        <template #footer>
                            <van-button size="small" type="primary" @click.stop="navigateToScore(match.id)" round>
                                查看分数
                            </van-button>
                        </template>
                    </van-card>
                </van-list>
            </van-pull-refresh>
        </div>
    </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted } from 'vue';
import { showToast } from 'vant';
import userApi from '@/api/user';

const matches = ref([]);
const router = useRouter();
const loading = ref(false);
const finished = ref(false);
const isRefreshing = ref(false);
const error = ref(false);

onMounted(async () => {
    await loadMatchData();
});

const loadMatchData = async () => {
    try {
        error.value = false;
        const infos = await userApi.getMyMatchSubjectsInfo();
        matches.value = infos;
        finished.value = true; // 加载完所有数据
    } catch (err) {
        console.error('Failed to load match data:', err);
        error.value = true;
        showToast({
            message: '加载比赛数据失败',
            type: 'fail',
            position: 'top',
        });
    }
};

const onLoad = async () => {
    // 由于是一次性加载所有数据，这里直接设置加载完成
    finished.value = true;
    loading.value = false;
};

const onRefresh = async () => {
    try {
        await loadMatchData();
        showToast({
            message: '刷新成功',
            type: 'success',
            position: 'top',
        });
    } catch (error) {
        showToast({
            message: '刷新失败',
            type: 'fail',
            position: 'top',
        });
    } finally {
        isRefreshing.value = false;
    }
};

const navigateToScore = (id) => {
    router.push({ name: 'matchsubjectscore', query: { id: id } });
};

const backToPreviousPage = () => {
    router.back();
};

const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const getStatusType = (status) => {
    switch (status) {
        case 2:
            return 'primary';
        case 4:
            return 'success';
        case 3:
            return 'warning';
        default:
            return 'default';
    }
};
</script>

<style scoped>
.page-container {
    width: 100%;
    height: 100vh;
    overflow: hidden;
    background-color: #f7f8fa;
    position: relative;
}

.fixed-nav-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
}

/* 导航栏样式 */
:deep(.custom-nav-bar) {
    background: linear-gradient(135deg, var(--primary-color, #3f51b5) 0%, var(--primary-light-color, #5c6bc0) 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

:deep(.van-nav-bar) {
    height: 46px;
    line-height: 46px;
}

:deep(.van-nav-bar__title),
:deep(.van-nav-bar__text),
:deep(.van-nav-bar .van-icon),
:deep(.van-nav-bar__arrow) {
    color: white !important;
}

.match-list {
    position: relative;
    margin-top: 46px;
    /* 导航栏高度 */
    height: calc(100vh - 46px);
    background-color: #f7f8fa;
    box-sizing: border-box;
    overflow: hidden;
    /* 防止双滚动条 */
}

/* 确保列表内容在下拉刷新头部下方 */
:deep(.van-list) {
    position: relative;
    z-index: 2;
    background-color: #f7f8fa;
}

.match-card {
    margin-bottom: 8px;
    border-radius: 8px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    word-break: break-word;
    overflow-wrap: break-word;
    border: 1px solid #ebedf0;
}

.match-card:nth-child(even) {
    background-color: #e8e5f1;
    border: 1px solid #e8eaed;
}

.match-card:active {
    transform: scale(0.98);
    border-color: #dcdee0;
}

.match-title {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: 4px;
    width: 100%;
    gap: 8px;
}

.match-name {
    font-size: 16px;
    font-weight: 500;
    color: #323233;
    line-height: 1.3;
    white-space: normal;
    word-break: break-word;
    text-align: left;
    flex: 1;
}

.status-tag {
    flex-shrink: 0;
    margin-top: 2px;
}

.match-info {
    color: #646566;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE and Edge */
}

.match-info::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari and Opera */
}

.info-row {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    flex-shrink: 0;
    padding-right: 4px;
}

.info-row:last-child {
    padding-right: 0;
}

.info-row span {
    color: #646566;
}

.match-info .info-row :deep(.van-icon.info-icon) {
    margin-right: 4px;
    color: #666666 !important;
    flex-shrink: 0;
    font-size: 14px;
}

/* 确保导航栏的图标不受影响 */
.van-nav-bar :deep(.van-icon) {
    color: white !important;
}


:deep(.van-pull-refresh) {
    height: 100%;
    background-color: #f7f8fa;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    /* Firefox */
    scrollbar-color: #dcdee0 #f7f8fa;
    /* Firefox */
}

/* Webkit浏览器的滚动条样式 */
:deep(.van-pull-refresh::-webkit-scrollbar) {
    width: 8px;
}

:deep(.van-pull-refresh::-webkit-scrollbar-track) {
    background: #f7f8fa;
    border-radius: 4px;
}

:deep(.van-pull-refresh::-webkit-scrollbar-thumb) {
    background-color: #dcdee0;
    border-radius: 4px;
    border: 2px solid #f7f8fa;
}

:deep(.van-pull-refresh::-webkit-scrollbar-thumb:hover) {
    background-color: #c8c9cc;
}

:deep(.van-pull-refresh__track) {
    min-height: 100%;
    padding: 8px 8px 80px;
    box-sizing: border-box;
}

:deep(.van-pull-refresh__head) {
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 1;
}

:deep(.van-pull-refresh__loading) {
    margin-top: 20px;
}

:deep(.van-card) {
    padding: 12px;
    cursor: pointer;
    background-color: transparent;
    margin-bottom: 8px;
    width: 100%;
    box-sizing: border-box;
}

:deep(.van-card__header) {
    display: block !important;
}

:deep(.van-card__title) {
    text-align: left;
    width: 100%;
    margin: 0;
}

:deep(.van-card__desc) {
    text-align: left;
    width: 100%;
    margin: 0;
}

:deep(.van-card__content) {
    text-align: left !important;
}

:deep(.van-card__header) {
    padding: 0;
}

:deep(.van-card__content) {
    padding: 0;
}

:deep(.van-card__footer) {
    margin-top: 8px;
    text-align: right;
}

:deep(.van-button--small) {
    min-width: 80px;
    height: 28px;
    padding: 0 12px;
    font-size: 13px;
}

:deep(.van-button--primary) {
    background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
    border: none;
}

:deep(.van-list__finished-text),
:deep(.van-list__error-text),
:deep(.van-empty__description) {
    color: #969799;
    font-size: 14px;
    padding: 16px 0;
}

:deep(.van-empty) {
    padding: 32px 0;
}

:deep(.van-pull-refresh__track) {
    padding-bottom: 16px;
}
</style>